import { useState, useCallback, useMemo } from "react";
import {
  ExternalLink,
  SlidersHorizontal,
  Check,
  ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { Link, useParams } from "@tanstack/react-router";
import * as Popover from "@radix-ui/react-popover";
import * as Checkbox from "@radix-ui/react-checkbox";
import { useTranslation } from "@/packages/utils";
import { useDocumentCategoryLabel } from "@/components/documents/use-document-category-label";
import type { Id } from "@/packages/types";
import { api } from "convex/_generated/api";
import { useWorkspaceQuery } from "@/hooks";
import { getPaletteColor } from "@/lib/color-palettes";

type Urgency = "red" | "yellow" | "green";

const URGENCY_OPTIONS: {
  level: Urgency;
  dotClass: string;
  labelKey:
    | "dashboard:documentDeadlines.filter.critical"
    | "dashboard:documentDeadlines.filter.soon"
    | "dashboard:documentDeadlines.filter.ok";
}[] = [
  {
    level: "red",
    dotClass: "bg-red-500",
    labelKey: "dashboard:documentDeadlines.filter.critical",
  },
  {
    level: "yellow",
    dotClass: "bg-amber-400",
    labelKey: "dashboard:documentDeadlines.filter.soon",
  },
  {
    level: "green",
    dotClass: "bg-emerald-500",
    labelKey: "dashboard:documentDeadlines.filter.ok",
  },
];

/** Returns palette hex for urgency text — use with inline style={{ color: ... }} */
function urgencyTextColor(urgency: Urgency): string {
  return getPaletteColor("deadlines", URGENCY_SLOT[urgency]);
}

const URGENCY_SLOT: Record<Urgency, number> = { red: 1, yellow: 2, green: 3 };

function urgencyBarColor(urgency: Urgency): string {
  return getPaletteColor("deadlines", URGENCY_SLOT[urgency]);
}

function urgencyFill(daysRemaining: number): number {
  if (daysRemaining <= 0) return 100;
  if (daysRemaining >= 90) return 5;
  return Math.max(5, 100 - Math.round((daysRemaining / 90) * 100));
}

function formatDeadlineDate(timestamp: number): string {
  const d = new Date(timestamp);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

type UrgencyStatusKey =
  | "documentDeadlines.status.overdue"
  | "documentDeadlines.status.critical"
  | "documentDeadlines.status.soon"
  | "documentDeadlines.status.ok";

function getUrgencyStatusKey(
  urgency: Urgency,
  daysRemaining: number,
): UrgencyStatusKey {
  if (daysRemaining <= 0) return "documentDeadlines.status.overdue";
  const slot = URGENCY_SLOT[urgency];
  if (slot === 1) return "documentDeadlines.status.critical";
  if (slot === 2) return "documentDeadlines.status.soon";
  return "documentDeadlines.status.ok";
}

// ── Document type from query ───────────────────────────────────
interface DeadlineDoc {
  documentId: Id<"documents">;
  fileName: string;
  urgency: Urgency;
  daysRemaining: number;
  expirationDate: number;
  currentVersion?: number | null;
  categoryName?: string | null;
  categorySlug?: string | null;
  linkedAudits: { id: string; name: string }[];
}

// ── Expanded Detail ────────────────────────────────────────────

function ExpandedDetail({ doc }: { doc: DeadlineDoc }) {
  const { workspaceSlug } = useParams({ strict: false });
  const { t } = useTranslation(["dashboard"]);
  const getCategoryLabel = useDocumentCategoryLabel();
  const statusKey = getUrgencyStatusKey(doc.urgency, doc.daysRemaining);
  const slot = daysRemainingSlot(doc.daysRemaining, doc.urgency);
  const color = getPaletteColor("deadlines", slot);

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={springs.snappy}
      className="overflow-hidden"
    >
      <div className="mt-3 pt-3 border-t border-border/40 space-y-2.5">
        <div className="grid grid-cols-3 gap-x-4 gap-y-2">
          <div>
            <span className="text-[11px] text-muted">
              {t("documentDeadlines.expiresLabel")}
            </span>
            <p className="text-sm font-medium text-primary">
              {formatDeadlineDate(doc.expirationDate)}
            </p>
          </div>
          {doc.currentVersion != null && (
            <div>
              <span className="text-[11px] text-muted">
                {t("documentDeadlines.versionLabel")}
              </span>
              <p className="text-sm font-medium text-primary">
                v{doc.currentVersion}
              </p>
            </div>
          )}
          {doc.categoryName && (
            <div>
              <span className="text-[11px] text-muted">
                {t("documentDeadlines.categoryLabel")}
              </span>
              <p className="text-sm font-medium text-primary">
                {getCategoryLabel({
                  name: doc.categoryName ?? "",
                  slug: doc.categorySlug,
                })}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 overflow-x-auto">
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full shrink-0"
              style={{ color, backgroundColor: `${color}15` }}
            >
              {t(statusKey)}
            </span>
            {doc.linkedAudits.map((audit) => (
              <span
                key={audit.id}
                className="text-[11px] px-1.5 py-0.5 rounded-md bg-card border border-border text-secondary shrink-0"
              >
                {audit.name}
              </span>
            ))}
            {doc.linkedAudits.length === 0 && (
              <span className="text-[11px] text-muted">
                {t("documentDeadlines.noLinkedAudits")}
              </span>
            )}
          </div>
          {workspaceSlug && (
            <Link
              to="/$workspaceSlug/documents/$documentId"
              params={{
                workspaceSlug: String(workspaceSlug),
                documentId: doc.documentId,
              }}
              className="shrink-0 ml-2 text-accent hover:text-accent/80 transition-colors"
              title={t("documentDeadlines.openDocument")}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function daysRemainingSlot(daysRemaining: number, urgency: Urgency): number {
  return daysRemaining <= 0 ? 1 : URGENCY_SLOT[urgency];
}

// ── Deadline Card ──────────────────────────────────────────────

function DeadlineCard({
  doc,
  index,
  expanded,
  onToggle,
}: {
  doc: DeadlineDoc;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(["dashboard"]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.snappy, delay: index * 0.05 }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left rounded-2xl bg-surface px-5 py-3.5 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-medium text-primary truncate mr-2">
            {doc.fileName}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className="text-sm font-bold tabular-nums"
              style={{ color: urgencyTextColor(doc.urgency) }}
            >
              {doc.daysRemaining <= 0
                ? t("documentDeadlines.overdue")
                : t("documentDeadlines.days", {
                    count: doc.daysRemaining,
                  })}
            </span>
            <motion.div
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={14} className="text-muted" />
            </motion.div>
          </div>
        </div>

        <div className="h-1.5 w-full rounded-full bg-surface overflow-hidden border border-border/30">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: urgencyBarColor(doc.urgency) }}
            initial={{ width: 0 }}
            animate={{ width: `${urgencyFill(doc.daysRemaining)}%` }}
            transition={{ ...springs.smooth, delay: index * 0.06 }}
          />
        </div>

        <AnimatePresence>
          {expanded && <ExpandedDetail doc={doc} />}
        </AnimatePresence>
      </button>
    </motion.div>
  );
}

// ── Ghost empty state ─────────────────────────────────────────

function DeadlinesGhost() {
  const ghostDocs = [
    { nameW: "w-36", daysW: "w-12", barFill: "75%", barColor: "#f87171" },
    { nameW: "w-44", daysW: "w-14", barFill: "45%", barColor: "#fbbf24" },
    { nameW: "w-28", daysW: "w-10", barFill: "15%", barColor: "#34d399" },
  ];

  return (
    <div className="space-y-2 opacity-15">
      {ghostDocs.map((doc, i) => (
        <div
          key={`ghost-dl-${i.toString()}`}
          className="rounded-2xl bg-surface px-5 py-3.5 shadow-sm"
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`${doc.nameW} h-3.5 bg-card-hover rounded`} />
            <div className={`${doc.daysW} h-3.5 bg-card-hover rounded`} />
          </div>
          <div className="h-1.5 w-full rounded-full bg-surface overflow-hidden border border-border/30">
            <div
              className="h-full rounded-full"
              style={{
                backgroundColor: doc.barColor,
                width: doc.barFill,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Skeleton ───────────────────────────────────────────────────

function SkeletonDeadlines() {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-2xl bg-surface px-5 py-3.5 shadow-sm animate-pulse"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-40 h-3.5 bg-card-hover rounded" />
            <div className="w-14 h-3.5 bg-card-hover rounded" />
          </div>
          <div className="h-1.5 bg-card-hover rounded-full" />
        </div>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────

export function ExpiryWarnings() {
  const { t } = useTranslation(["dashboard"]);

  const { data, isLoading } = useWorkspaceQuery(
    api.dashboard.queries.documentDeadlines.get,
    {},
  );

  const allDocuments = data ?? [];

  const [expandedSet, setExpandedSet] = useState<Set<Id<"documents">>>(
    () => new Set(),
  );
  const [urgencyFilter, setUrgencyFilter] = useState<Set<Urgency>>(
    () => new Set<Urgency>(["red", "yellow", "green"]),
  );
  const [popoverOpen, setPopoverOpen] = useState(false);

  const documents = useMemo(
    () => allDocuments.filter((doc) => urgencyFilter.has(doc.urgency)),
    [allDocuments, urgencyFilter],
  );

  const toggleExpand = useCallback((documentId: Id<"documents">) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  const handleToggleUrgency = useCallback(
    (level: Urgency, checked: boolean) => {
      setUrgencyFilter((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(level);
        } else if (next.size > 1) {
          next.delete(level);
        }
        return next;
      });
    },
    [],
  );

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden h-full flex flex-col">
      <div className="flex items-center px-4 pt-3 pb-3 shrink-0">
        <h3 className="text-sm font-semibold text-primary">
          {t("documentDeadlines.title")}
        </h3>
        <div className="ml-auto">
          <Popover.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
            <Popover.Trigger asChild>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-secondary hover:text-primary hover:bg-surface transition-colors"
                title={t("documentDeadlines.filterButton")}
              >
                <SlidersHorizontal size={14} />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                side="bottom"
                align="end"
                sideOffset={8}
                className="z-50 w-56 rounded-xl border border-border bg-card p-3 shadow-lg"
              >
                <p className="text-xs font-semibold text-secondary mb-2">
                  {t("documentDeadlines.filterLabel")}
                </p>
                <div className="flex flex-col gap-1.5">
                  {URGENCY_OPTIONS.map(({ level, dotClass, labelKey }) => {
                    const isChecked = urgencyFilter.has(level);
                    const isLastChecked = isChecked && urgencyFilter.size === 1;

                    return (
                      <label
                        key={level}
                        htmlFor={`deadline-filter-${level}`}
                        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${
                          isLastChecked
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer hover:bg-surface"
                        }`}
                      >
                        <Checkbox.Root
                          id={`deadline-filter-${level}`}
                          checked={isChecked}
                          disabled={isLastChecked}
                          onCheckedChange={(checked) =>
                            handleToggleUrgency(level, checked === true)
                          }
                          className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-surface transition-colors data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                        >
                          <Checkbox.Indicator>
                            <Check size={12} className="text-white" />
                          </Checkbox.Indicator>
                        </Checkbox.Root>
                        <span
                          className={`w-2 h-2 rounded-full shrink-0 ${dotClass}`}
                        />
                        <span className="text-primary text-sm">
                          {t(labelKey)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="px-4 pb-3"
          >
            <SkeletonDeadlines />
          </motion.div>
        ) : documents.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex items-center justify-center px-4 pb-3"
          >
            <DeadlinesGhost />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="expiry-body overflow-y-auto flex-1 min-h-0 px-4 pb-3 flex flex-col"
          >
            <div className="space-y-2">
              {documents.map((doc, i) => (
                <DeadlineCard
                  key={doc.documentId}
                  doc={doc}
                  index={i}
                  expanded={expandedSet.has(doc.documentId)}
                  onToggle={() => toggleExpand(doc.documentId)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
